import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export enum Panel {
  FLOW_PROPERTIES = 'FLOW_PROPERTIES',
  SETTINGS = 'SETTINGS',
  PROJECT_DATA = 'PROJECT_DATA',
  PERFORMANCE_GUARANTEE = 'PERFORMANCE_GUARANTEE',
  RISK_ASSESSMENT = 'RISK_ASSESSMENT',
  EQUIPMENT_SELECTION = 'EQUIPMENT_SELECTION',
}

export interface ChamberAnalysisActionPayload {
  unitId: string;
  running: boolean;
}

export interface ChamberAnalysisErrorPayload {
  unitId: string;
  failed: boolean;
}

export interface LayoutInterface {
  openPanel: Panel | null;
  showSideCard: boolean;
  showReportPanel: boolean;
  flowsheetNoChangedModalOpen: boolean;
  isPrinting: boolean;
  runningChamberAnalysisSimulations: Set<string>;
  failedChamberAnalysisSimulations: Set<string>;
  showApproveFileModal: boolean;
  skipsDiagramUpdate: boolean;
  riskAssessmentSelectedUnits: string[];
}

const initialState: LayoutInterface = {
  openPanel: null,
  showSideCard: false,
  showReportPanel: false,
  flowsheetNoChangedModalOpen: false,
  isPrinting: false,
  runningChamberAnalysisSimulations: new Set(),
  failedChamberAnalysisSimulations: new Set(),
  showApproveFileModal: false,
  skipsDiagramUpdate: false,
  riskAssessmentSelectedUnits: [],
};

const layoutSlice = createSlice({
  name: 'layoutState',
  initialState,
  reducers: {
    setOpenPanel(state, action: PayloadAction<Panel | null>) {
      if (state.openPanel === action.payload) {
        state.openPanel = null;
      } else {
        state.openPanel = action.payload;
        state.showReportPanel = false;
        state.showSideCard = false;
      }
    },
    setShowSideCard(state, action: PayloadAction<boolean>) {
      state.showSideCard = action.payload;
    },
    setShowReportPanel(state, action: PayloadAction<boolean>) {
      state.showReportPanel = action.payload;
    },
    setFlowsheetNoChangedModalOpen(state, action: PayloadAction<boolean>) {
      state.flowsheetNoChangedModalOpen = action.payload;
    },
    setIsPrinting(state, action: PayloadAction<boolean>) {
      state.isPrinting = action.payload;
    },
    setRunningChamberAnalysisSimulation(
      state,
      action: PayloadAction<ChamberAnalysisActionPayload>,
    ) {
      if (action.payload.running) {
        state.runningChamberAnalysisSimulations.add(action.payload.unitId);
      } else {
        state.runningChamberAnalysisSimulations.delete(action.payload.unitId);
      }
    },
    setFailedChamberAnalysisSimulation(
      state,
      action: PayloadAction<ChamberAnalysisErrorPayload>,
    ) {
      if (action.payload.failed) {
        state.failedChamberAnalysisSimulations.add(action.payload.unitId);
      } else {
        state.failedChamberAnalysisSimulations.delete(action.payload.unitId);
      }
    },
    setShowApproveFileModal(state, action: PayloadAction<boolean>) {
      state.showApproveFileModal = action.payload;
    },
    setSkipsDiagramUpdate(state, action: PayloadAction<boolean>) {
      state.skipsDiagramUpdate = action.payload;
    },
    setRiskAssessmentSelectedUnits(state, action: PayloadAction<string[]>) {
      state.riskAssessmentSelectedUnits = action.payload;
    },
    removeRiskAssessmentUnit(state, action: PayloadAction<string>) {
      state.riskAssessmentSelectedUnits =
        state.riskAssessmentSelectedUnits.filter((id) => id !== action.payload);
    },
  },
});

export const {
  setOpenPanel,
  setShowSideCard,
  setShowReportPanel,
  setFlowsheetNoChangedModalOpen,
  setIsPrinting,
  setRunningChamberAnalysisSimulation,
  setFailedChamberAnalysisSimulation,
  setShowApproveFileModal,
  setSkipsDiagramUpdate,
  setRiskAssessmentSelectedUnits,
  removeRiskAssessmentUnit,
} = layoutSlice.actions;

export const layoutReducer = layoutSlice.reducer;
